import React, { useEffect, useRef, useState } from 'react'

import { Button,Input } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import { Icon } from '@/icons';
import { vars } from 'style/theme.css'
import { useNavigate } from 'react-router-dom';
import Pagination from 'components/atoms/Pagination/Pagination';
import { academyStudentApi } from '@/services/api';
import { formatNumber } from '@/utils/number';
import { Modal } from '@/components/organisms/Modal/Modal';
import { useSessionSlug } from '@/hooks/useSessionSlug';
type StatusFilter = 'ALL' | 'ENROLLED' | 'WITHDRAWN';

interface StudentRow {
  id: number
  no: number
  name: string
  studentId: string
  className: string
  birthDate: string
  status: '수강중' | '퇴원' | '-'
  registrationDate: string
}
  
  // 컬럼 렌더링용 메타 정보
const columns = [
  { key: 'id'     as const, render: (row: StudentRow) => formatNumber(row.no) },
    { key: 'name'   as const, render: (row: StudentRow) => row.name },
    { key: 'studentId' as const, render: (row: StudentRow) => (
      <div className="flex gap-2 items-center lg:block">
        <span>{row.studentId}</span>
      </div>
    ) },
    { key: 'className' as const, render: (row: StudentRow) => (
      <div className="flex gap-2 items-center lg:block">
        <span>{row.className}</span>
      </div>
    ) },
    { key: 'birthDate' as const, render: (row: StudentRow) => (
      <div className="flex gap-2 items-center lg:block">
        <span>{row.birthDate}</span>
      </div>
    ) },
    { key: 'status' as const, render: (row: StudentRow) => renderStatus(row.status) },
    { key: 'registrationDate' as const, render: (row: StudentRow) => (
      <div className="flex gap-2 items-center lg:block">
        <span>{row.registrationDate}</span>
      </div>
    ) },
  ]
  
  function renderStatus(status: StudentRow['status']) {
    switch (status) {
      case '수강중':
        return <span className="font-medium text-blue-500">수강중</span>
      case '퇴원':
        return <span className="font-medium text-gray-600">퇴원</span>
      default:
        return <span>-</span>
    }
  }

export default function AcademyStudentList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const pageSize = 10;
  const { academyCode: sessionAcademyCode, status: sessionStatus } = useSessionSlug();
  const academyCode = sessionAcademyCode || '';
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const fetchList = async (page: number, nextStatus?: StatusFilter) => {
    if (!academyCode) return;
    const effectiveStatus = nextStatus ?? statusFilter;
    const statusParam = effectiveStatus === 'ALL' ? undefined : effectiveStatus;
    const res: any = await academyStudentApi.list({ academyCode, q: searchText, status: statusParam, page: page - 1, size: pageSize });
    const pageData = res?.data || res;
    const content = pageData?.content || [];
    const totalElements = pageData?.totalElements || 0;
    const mapped: StudentRow[] = content.map((item: any, idx: number) => ({
      id: item.academyStudentId ?? item.id ?? idx + 1,
      no: totalElements > 0 ? totalElements - ((page - 1) * pageSize + idx) : content.length - idx,
      name: item.name,
      studentId: item.studentLoginId,
      className: item.className || '',
      birthDate: item.birthDate ? String(item.birthDate) : '',
      status: (item.status as any) || '-',
      registrationDate: item.registrationDate ? String(item.registrationDate).replace('T', ' ').slice(0, 16) : ''
    }));
    setRows(mapped);
    setTotal(totalElements);
    setCurrent(page);
  };

  const didInitRef = useRef(false);
  useEffect(() => {
    if (sessionStatus !== 'ready') return;
    if (!academyCode || didInitRef.current) return;
    didInitRef.current = true;
    const sp = new URLSearchParams(window.location.search);
    const initial = Math.max(1, Number(sp.get('page') || '1'));
    fetchList(initial).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academyCode, sessionStatus]);

  useEffect(() => {
    if (!academyCode || !didInitRef.current) return;
    fetchList(1, statusFilter).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);
  

  const handleRowClick = (studentId: number) => {
    navigate(`/academy/student/detail/${studentId}?from=studentList&page=${current}`);
  };
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = async (e) => {
    if (e.key === 'Enter') {
      await fetchList(1);
    }
  };
  const handleSearchClick = async () => {
    await fetchList(1);
  };
  
  const confirmGuardianConsent = async () => {
    return Modal.confirm({
      message: '본인은 만 14세 미만의 학생에 대한 학부모(법정대리인) 동의를 받고\n계정 정보를 등록함을 확인합니다.',
      okText: '동의',
      cancelText: '취소',
    });
  };
  
  return (
    <main className='flex flex-col flex-1'>
        <div className='flex flex-row justify-between items-center pt-8'>
            <h1 className='heading1'>학생관리</h1>
        </div>
    <section className='flex flex-wrap gap-2 justify-between py-2 pt-3 w-full'>
        <div className='flex flex-[650px] gap-2'>
                <Input
                placeholder='학생명, ID, 유형(클래스), 학생상태로 검색'
                className='max-w-[350px]'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={handleKeyDown}
                icon={<Icon name="search" size={20} color={vars.color.gray[300]} />}
                />
                <Button outline onClick={handleSearchClick}>검색</Button>
                <select
                  className='px-3 py-2 text-sm text-gray-700 rounded-md border border-gray-200'
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                >
                  <option value='ALL'>전체 상태</option>
                  <option value='ENROLLED'>수강중만</option>
                  <option value='WITHDRAWN'>퇴원만</option>
                </select>
        </div>
        <div className='flex gap-2 justify-end'>
            <Button
              className='flex gap-1 items-center'
              outline
              onClick={async () => {
                const blob = await academyStudentApi.downloadTemplate();
                const url = window.URL.createObjectURL(new Blob([blob]));
                const link = document.createElement('a');
                link.href = url;
                link.download = 'student_template.xlsx';
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
              }}
            >
                <Icon name="download" size={20} />
                <p>학생 계정생성 양식 다운로드</p>
                </Button>
            <Button
              onClick={async () => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.xlsx,.xls';
                input.onchange = async () => {
                  const file = input.files?.[0];
                  if (!file) return;
                  const consent = await confirmGuardianConsent();
                  if (!consent) return;
                  try {
                  await academyStudentApi.uploadExcel({ file });
                    await Modal.alert({
                      message: '업로드 및 등록이 완료되었습니다.',
                      okText: '확인',
                    });
                  await fetchList(1);
                  } catch (e: any) {
                    await Modal.alert({
                      title: '업로드 실패',
                      message: e?.response?.data?.message || '학생 계정 업로드 중 오류가 발생했습니다.',
                      okText: '확인',
                    });
                  }
                };
                input.click();
              }}
            >학생 계정생성 엑셀 업로드</Button>
        </div>
            </section>
    <section className='flex flex-col flex-1 justify-between pt-5 w-full'>
                {/* no_list */}
                {/* <p>리스트가 없습니다.</p> */}
                <div className='w-full'>
                    {/* 테이블 해더 pc 용*/}
                    <ul className={`${styles.academyHeaderList} w-full`}>
                        <li className={`${styles.headerItem} max-w-[60px] justify-center`}>순번</li>
                        <li className={`${styles.headerItem} w-full`}>학생명</li>
                        <li className={`${styles.headerItem} w-full`}>ID</li>
                        <li className={`${styles.headerItem} w-full`}>클래스</li>
                        <li className={`${styles.headerItem} w-full`}>생년월일</li>
                        <li className={`${styles.headerItem} w-full`}>학생상태</li>
                        <li className={`${styles.headerItem} w-full`}>등록일</li>
                    </ul>
                    <div className='flex flex-col w-full lg:gap-0'>
                        {rows.map((row) => (
                            <ul 
                                key={row.id} 
                                className={`${styles.academyBodyList}  cursor-pointer hover:bg-gray-50 transition-colors`}
                                onClick={() => handleRowClick(row.id)}
                            >
                            {columns.map(({ key, render }) => (
                                <li
                                key={key}
                                className={`${styles.bodyItem} ${
                                    key === 'id' ? 'max-w-[60px] justify-center' :
                                    key === 'name' ? 'col-span-2 lg:col-span-1 w-full ' :
                                    key === 'studentId' ? 'text-sm text-gray-500 w-full ' :
                                    key === 'className' ? 'text-sm text-gray-500 w-full' :
                                    key === 'birthDate' ? 'text-sm text-gray-500 w-full ' :
                                    key === 'status' ? ' w-full  ' :
                                    key === 'registrationDate' ? 'text-sm text-gray-500 w-full lg:static col-span-1' :
                                    ''
                                }`}
                                >
                                {render(row)}
                                </li>
                            ))}
                            </ul>
                        ))}
                    </div>
                </div>
                    <div className='py-5'>
                <Pagination
                total={total}
                pageSize={pageSize}
                current={current}
                onChange={(p)=>fetchList(p)}
                />
                </div>
            </section>
    </main>
  )
}
